import { ActionIcon, Button, Group, Image, Stack, Text } from '@mantine/core'
import { Dropzone, FileWithPath, MIME_TYPES } from '@mantine/dropzone'
import * as Sentry from '@sentry/react'
import { IconPhoto, IconUpload, IconX } from '@tabler/icons-react'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import {
  useBranchImagesQuery,
  useDeleteBranchImageMutation,
  useUploadBranchImageMutation,
} from '../../../../api/branches/api'
import { showNotificationWithSuccess } from '../../../../api/helpers'
import { MAX_FILE_UPLOAD_SIZE } from '../../../../constants/constants'
import { useUser } from '../../../../user-provider/user-context'

export const Route = createFileRoute('/app/settings/branch-settings/branch-identity')({
  component: BranchIdentityComponent,
})

type PendingImage = {
  file: FileWithPath
  previewUrl: string
  isCover: boolean
}

type PendingDelete = {
  imageId: string
  isCover: boolean
}

function BranchIdentityComponent() {
  const { branchInfo } = useUser()
  const branchId = branchInfo?.provider_branch_id || ''
  const { data: branchImages, isLoading: branchLoading, isError: branchError } = useBranchImagesQuery(branchId)
  const uploadImageMutation = useUploadBranchImageMutation()
  const deleteImageMutation = useDeleteBranchImageMutation()

  // State for pending changes
  const [pendingUploads, setPendingUploads] = useState<PendingImage[]>([])
  const [pendingDeletes, setPendingDeletes] = useState<PendingDelete[]>([])

  // Reset pending changes when data loads
  useEffect(() => {
    if (branchImages) {
      setPendingUploads([])
      setPendingDeletes([])
    }
  }, [branchImages])

  const handleBannerDrop = (files: FileWithPath[]) => {
    const file = files[0]
    if (file) {
      const previewUrl = URL.createObjectURL(file)
      setPendingUploads((prev) => [...prev, { file, previewUrl, isCover: true }])
    }
  }

  const handleGalleryDrop = (files: FileWithPath[]) => {
    files.forEach((file) => {
      const previewUrl = URL.createObjectURL(file)
      setPendingUploads((prev) => [...prev, { file, previewUrl, isCover: false }])
    })
  }

  const handleDeleteImage = (imageId: string, isCover: boolean) => {
    setPendingDeletes((prev) => [...prev, { imageId, isCover }])
  }

  const handleRemovePendingUpload = (index: number) => {
    setPendingUploads((prev) => {
      const newUploads = [...prev]
      URL.revokeObjectURL(newUploads[index].previewUrl)
      newUploads.splice(index, 1)
      return newUploads
    })
  }

  const handleSaveChanges = async () => {
    if (!branchId) return

    try {
      // Handle deletes first
      for (const pendingDelete of pendingDeletes) {
        await deleteImageMutation.mutateAsync({
          branchId,
          imageId: pendingDelete.imageId,
        })
      }

      for (const pendingUpload of pendingUploads) {
        await uploadImageMutation.mutateAsync({
          branchId,
          file: pendingUpload.file,
          isCover: pendingUpload.isCover,
        })
      }

      showNotificationWithSuccess('Images uploaded successfully', 'Success')
      setPendingUploads([])
      setPendingDeletes([])
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  const hasChanges = pendingUploads.length > 0 || pendingDeletes.length > 0
  const hasPendingBannerUpload = pendingUploads.some((pu) => pu.isCover)
  const isSaving = uploadImageMutation.isPending || deleteImageMutation.isPending

  // Get current banner image (excluding pending deletes)
  const currentBannerImage =
    branchImages?.cover_image && !pendingDeletes.some((pd) => pd.imageId === branchImages.cover_image?.id)
      ? branchImages.cover_image
      : null

  // Get current gallery images (excluding pending deletes)
  const currentGalleryImages = (branchImages?.images || []).filter(
    (image) => !pendingDeletes.some((pd) => pd.imageId === image.id),
  )

  // Get pending banner uploads
  const pendingBannerUploads = pendingUploads.filter((pu) => pu.isCover)

  // Get pending gallery uploads
  const pendingGalleryUploads = pendingUploads.filter((pu) => !pu.isCover)

  return (
    <Stack>
      <div>
        <Text size="sm" fw={500} mb="xs">
          Banner image
        </Text>
        {currentBannerImage ? (
          <Group mb="md" p="sm">
            <Image src={currentBannerImage.url} alt="Banner" fit="cover" h={100} radius="md" flex={1} />
            <div>
              <Text size="sm" fw={500}>
                Banner.jpg
              </Text>
            </div>
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => handleDeleteImage(currentBannerImage.id, true)}
              disabled={branchLoading || branchError}
            >
              <IconX size={16} />
            </ActionIcon>
          </Group>
        ) : hasPendingBannerUpload ? (
          pendingBannerUploads.map((pendingUpload, index) => (
            <Group key={`banner-${index}`} mb="md" p="sm">
              <Image src={pendingUpload.previewUrl} alt="Pending Banner" fit="cover" h={100} radius="md" flex={1} />
              <div>
                <Text size="sm" fw={500}>
                  {pendingUpload.file.name}
                </Text>
                <Text size="xs" c="dimmed">
                  (Pending upload)
                </Text>
              </div>
              <ActionIcon
                variant="subtle"
                color="red"
                onClick={() => handleRemovePendingUpload(pendingUploads.findIndex((pu) => pu === pendingUpload))}
              >
                <IconX size={16} />
              </ActionIcon>
            </Group>
          ))
        ) : (
          <MediaDropzone onDrop={handleBannerDrop} disabled={branchLoading || branchError} multiple={false} />
        )}
      </div>

      <div>
        <Text size="sm" fw={500} mb="xs">
          Shop gallery photos
        </Text>
        <Text size="xs" c="dimmed" mb="md">
          Displayed to customers on your service center details page
        </Text>

        {/* Show current gallery images */}
        {currentGalleryImages.length > 0 && (
          <Stack mb="md">
            {currentGalleryImages.map((image) => (
              <Group key={image.id} justify="space-between" p="sm">
                <Group>
                  <Image src={image.url} alt="Gallery" fit="cover" h={100} radius="md" flex={1} />
                  <div>
                    <Text size="sm" fw={500}>
                      image{currentGalleryImages.indexOf(image) + 1}.jpg
                    </Text>
                  </div>
                </Group>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => handleDeleteImage(image.id, false)}
                  disabled={branchLoading || branchError}
                >
                  <IconX size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        )}

        {/* Show pending gallery uploads */}
        {pendingGalleryUploads.map((pendingUpload, index) => (
          <Group key={`gallery-${index}`} mb="md" p="sm">
            <Image src={pendingUpload.previewUrl} alt="Pending Gallery" fit="cover" h={100} radius="md" flex={1} />
            <div>
              <Text size="sm" fw={500}>
                {pendingUpload.file.name}
              </Text>
              <Text size="xs" c="dimmed">
                (Pending upload)
              </Text>
            </div>
            <ActionIcon
              variant="subtle"
              color="red"
              onClick={() => handleRemovePendingUpload(pendingUploads.findIndex((pu) => pu === pendingUpload))}
            >
              <IconX size={16} />
            </ActionIcon>
          </Group>
        ))}

        <MediaDropzone onDrop={handleGalleryDrop} disabled={branchLoading || branchError} multiple={true} />
      </div>

      <Group justify="flex-end">
        <Button onClick={handleSaveChanges} loading={isSaving} disabled={branchLoading || branchError || !hasChanges}>
          Save changes
        </Button>
      </Group>
    </Stack>
  )
}

const MediaDropzone = ({
  onDrop,
  disabled,
  multiple,
}: {
  onDrop: (files: FileWithPath[]) => void
  disabled: boolean
  multiple: boolean
}) => (
  <Dropzone
    onDrop={onDrop}
    onReject={(files) => console.log('rejected files', files)}
    maxSize={MAX_FILE_UPLOAD_SIZE}
    accept={[MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.heic]}
    multiple={multiple}
    disabled={disabled}
  >
    <Group justify="center" gap="xl" mih={100} style={{ pointerEvents: 'none' }}>
      <Dropzone.Accept>
        <IconUpload size={32} stroke={1.5} />
      </Dropzone.Accept>
      <Dropzone.Reject>
        <IconX size={32} stroke={1.5} />
      </Dropzone.Reject>
      <Dropzone.Idle>
        <IconPhoto size={32} stroke={1.5} />
      </Dropzone.Idle>

      <div>
        <Text size="sm" inline>
          Click to upload or drag and drop
        </Text>
        <Text size="xs" c="dimmed" inline mt={7}>
          Supported files: PNG, JPG, JPEG or HEIC (max 50MB)
        </Text>
      </div>
    </Group>
  </Dropzone>
)
